import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { useParams } from "react-router-dom";
import useSearchParams from "@hooks/use-search-params";

const useStages = () => { 
    const { platformId } = useParams();
    const [data, setData] = useState({
        companies : [],
        pagination: {},
        companyMap: {},
        history   : {}
    });

    const [stageList, setStageList] = useState([]);
    const [stageMap, setStageMap] = useState({});
    const [queryParams, setQueryParams] = useState({});
    const [modalConfig, setModalConfig] = useState({
        row   : {},
        isOpen: false
    });

    const handleModal = (row = {}) => {
        setModalConfig({
            row,
            isOpen: !modalConfig.isOpen
        });
    };
    const { axiosInstance, loading } = useAxios({ base: "panel" });
    const { axiosInstance: updateInstance, loading: updateLoading } = useAxios({
        base: "panel"
    });

    const { getSearchParams } = useSearchParams();

    const getAllStages = async () => {
        delete queryParams.tab;
        const { data: { clients, pagination } } = await axiosInstance.get(`/client/${platformId}/board-data`, {
            params: {
                ...queryParams,
                start: getSearchParams("start"),
                end  : getSearchParams("end")
            }
        });        
        setData(prev => ({
            ...prev,
            companies: clients,
            pagination,
            history  : {}
        }));
    };

    const getStageList = async () => {
        const response = await axiosInstance.get(`/filters/${platformId}/stages`);
        const stages = response?.data || [];
        setStageList(stages);
        const stageMapFromApi = {};
        stages.forEach(stage => {
            stageMapFromApi[stage._id] = stage;
        });

        setStageMap(stageMapFromApi);
    };

    const updateStage = async (id, company) => {
        await updateInstance.put(`/client/${platformId}/board/${id}`, {
            ...company
        });
        getAllStages();
    };

    const handleDragEnd = async result => {
        const { source, destination } = result;

        if (!destination) return;

        const sourceStage = source.droppableId;
        const destinationStage = destination.droppableId;

        const sourceCompany = [...data.companyMap[sourceStage]];
        const [movedCompany] = sourceCompany.splice(source.index, 1);

        if (sourceStage === destinationStage) {
            sourceCompany.splice(destination.index, 0, movedCompany);
            setData(prev => ({
                ...prev,
                companyMap: {
                    ...prev.companyMap,
                    [sourceStage]: sourceCompany
                }
            }));
        } else {
            const destinationList = Array.from(
                data.companyMap[destinationStage]
            );
            movedCompany.stage = destinationStage;
            destinationList.splice(destination.index, 0, movedCompany);

            setData(prev => ({
                ...prev,
                companyMap: {
                    ...prev.companyMap,
                    [sourceStage]     : sourceCompany,
                    [destinationStage]: destinationList
                }
            }));
          
            const payload = { stage: stageMap[destinationStage]._id };
            try {
                await updateStage(movedCompany._id, payload);
            } catch (error) {
                setData(prev => ({
                    ...prev,
                    companyMap: prev.companyMap
                }));
            }
        }
    };

    const handleDeleteStage = async id => {
        await updateInstance.delete(`/client/${platformId}/delete/${id}`);
        getAllStages();
    };
    
    useEffect(() => {
        const initializeData = async () => {
            await getStageList();
            setQueryParams({});
        };
        initializeData();
    }, []);

    useEffect(() => {
        if (Object.keys(queryParams).length >= 0) {
            getAllStages();
        }
    }, [queryParams]);

    useEffect(() => {
        if (!stageList.length) return;

        const categorizedData = {};

        stageList.forEach(stage => {
            categorizedData[stage._id] = [];
        });

        const activeClients = data.companies?.filter(client => client.status !== "delete") || [];

        activeClients.forEach(client => {
            let clientStageId = null;

            if (typeof client.stage === "object" && client.stage !== null) {
                clientStageId = client.stage._id || client.stage.id;
            } else {
                clientStageId = client.stage;
            }

            if (clientStageId in categorizedData) {
                categorizedData[clientStageId].push(client);
            } else {
                categorizedData[clientStageId] = [client];
            }
        });

        setData(prev => ({
            ...prev,
            companyMap: categorizedData
        }));
    }, [data.companies, stageList]);

    return {
        handleDragEnd,
        getAllStages,
        handleDeleteStage,
        setQueryParams,
        handleModal,
        modalConfig,
        loading,
        data,
        updateLoading,
        getStageList,
        stageMap,
        stageList
    };
};

export default useStages;
